/**
 * Admin-only form for injecting manual news signals into the qualification
 * engine. Visible only when the user is signed in (Emergent Google Auth).
 * Signals surface in the Europe/Title race row expansions + modify model
 * explanations via `qualification_modifiers.get_modifier`.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Newspaper, Trash, PlusCircle } from "@phosphor-icons/react";
import { useAuth } from "../contexts/AuthContext";
import { createNewsSignal, deleteNewsSignal, listNewsSignals } from "../lib/api";

const TOPICS = [
    "EUROPE_QUALIFICATION",
    "RELEGATION",
    "CUP_IMPACT",
    "EXTRA_CL_SLOT",
    "OFFICIAL_CONFIRMATION",
    "INJURY_CONTEXT",
    "ROTATION_CONTEXT",
];
const CONFIDENCE = ["LOW", "MEDIUM", "HIGH"];
const MODEL_ACTIONS = [
    "NO_CHANGE", "ADJUST_STATUS", "ADJUST_PROBABILITY",
    "ADD_EXPLANATION", "REQUIRE_MANUAL_REVIEW",
];

const EMPTY = {
    source: "",
    url: "",
    team_name: "",
    topic: "EUROPE_QUALIFICATION",
    headline: "",
    summary: "",
    confidence: "MEDIUM",
    impact_score: 50,
    model_action: "ADD_EXPLANATION",
};

export default function NewsSignalAdmin({ teamChoices = [] }) {
    const { user, login } = useAuth();
    const [form, setForm] = useState(EMPTY);
    const [signals, setSignals] = useState([]);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [expanded, setExpanded] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const data = await listNewsSignals({ limit: 50 });
            setSignals(data);
        } catch {
            setSignals([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        if (!user) {
            toast.error("Sign in to publish a news signal", {
                action: { label: "Sign in", onClick: login },
            });
            return;
        }
        if (form.headline.length < 3 || form.summary.length < 3) {
            toast.error("Headline and summary must be at least 3 characters");
            return;
        }
        setSubmitting(true);
        try {
            const payload = { ...form, impact_score: Number(form.impact_score) || 50 };
            if (!payload.url) delete payload.url;
            if (!payload.team_name) delete payload.team_name;
            await createNewsSignal(payload);
            toast.success("News signal published");
            setForm(EMPTY);
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Publish failed");
        } finally {
            setSubmitting(false);
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this signal?")) return;
        try {
            await deleteNewsSignal(id);
            toast.success("Signal removed");
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Delete failed");
        }
    };

    const teamOptions = useMemo(
        () => ["", ...Array.from(new Set(teamChoices)).sort()],
        [teamChoices],
    );

    return (
        <section data-testid="news-signal-admin">
            <div className="flex items-center justify-between gap-2 mb-3">
                <div className="flex items-center gap-2">
                    <Newspaper size={16} weight="fill" className="text-fa-cream" />
                    <h2 className="font-display font-black text-lg uppercase tracking-tight">
                        News Signals
                    </h2>
                    <span className="font-mono text-[10px] text-white/40">
                        ({signals.length} active)
                    </span>
                </div>
                <button
                    type="button"
                    onClick={() => setExpanded((v) => !v)}
                    data-testid="news-signal-toggle"
                    className="font-mono text-[10px] uppercase tracking-widest px-3 py-1 border border-white/20 hover:border-fa-cream hover:text-fa-cream transition-colors"
                >
                    {expanded ? "Hide" : "Manage"}
                </button>
            </div>

            {expanded && (
                <div className="border border-white/10 bg-[#0d0d0d] p-4 space-y-4">
                    {!user && (
                        <div className="border border-fa-cream/40 bg-fa-cream/5 p-3 font-mono text-[11px] text-fa-cream">
                            Sign in with Google to publish news signals.
                        </div>
                    )}

                    <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="news-signal-form">
                        <Field label="Source *">
                            <input
                                required
                                value={form.source}
                                onChange={(e) => update("source", e.target.value)}
                                placeholder="e.g. Daily Mail, Official PL, Tifo Football"
                                data-testid="news-input-source"
                                className={inputCls}
                            />
                        </Field>
                        <Field label="URL (optional)">
                            <input
                                type="url"
                                value={form.url}
                                onChange={(e) => update("url", e.target.value)}
                                placeholder="https://…"
                                data-testid="news-input-url"
                                className={inputCls}
                            />
                        </Field>
                        <Field label="Team">
                            <select
                                value={form.team_name}
                                onChange={(e) => update("team_name", e.target.value)}
                                data-testid="news-input-team"
                                className={selectCls}
                            >
                                {teamOptions.map((t) => (
                                    <option key={t} value={t}>{t || "— League-wide —"}</option>
                                ))}
                            </select>
                        </Field>
                        <Field label="Topic *">
                            <select
                                value={form.topic}
                                onChange={(e) => update("topic", e.target.value)}
                                data-testid="news-input-topic"
                                className={selectCls}
                            >
                                {TOPICS.map((t) => (
                                    <option key={t} value={t}>{t.replace(/_/g, " ")}</option>
                                ))}
                            </select>
                        </Field>
                        <Field label="Headline *" full>
                            <input
                                required
                                minLength={3}
                                maxLength={240}
                                value={form.headline}
                                onChange={(e) => update("headline", e.target.value)}
                                placeholder="Arsenal guarantee Champions League place with 3 games to spare"
                                data-testid="news-input-headline"
                                className={inputCls}
                            />
                        </Field>
                        <Field label="Summary *" full>
                            <textarea
                                required
                                minLength={3}
                                maxLength={1000}
                                rows={2}
                                value={form.summary}
                                onChange={(e) => update("summary", e.target.value)}
                                placeholder="Details that the model should surface in the relevant team's row…"
                                data-testid="news-input-summary"
                                className={`${inputCls} resize-y`}
                            />
                        </Field>
                        <Field label="Confidence">
                            <select
                                value={form.confidence}
                                onChange={(e) => update("confidence", e.target.value)}
                                data-testid="news-input-confidence"
                                className={selectCls}
                            >
                                {CONFIDENCE.map((c) => <option key={c} value={c}>{c}</option>)}
                            </select>
                        </Field>
                        <Field label="Model action">
                            <select
                                value={form.model_action}
                                onChange={(e) => update("model_action", e.target.value)}
                                data-testid="news-input-action"
                                className={selectCls}
                            >
                                {MODEL_ACTIONS.map((a) => (
                                    <option key={a} value={a}>{a.replace(/_/g, " ")}</option>
                                ))}
                            </select>
                        </Field>
                        <Field label="Impact score (0–100)">
                            <input
                                type="number"
                                min={0}
                                max={100}
                                value={form.impact_score}
                                onChange={(e) => update("impact_score", e.target.value)}
                                data-testid="news-input-impact"
                                className={inputCls}
                            />
                        </Field>
                        <div className="md:col-span-2 flex justify-end">
                            <button
                                type="submit"
                                disabled={submitting}
                                data-testid="news-signal-submit"
                                className="bg-fa-cream text-black font-mono text-xs uppercase tracking-widest px-4 py-2 flex items-center gap-1.5 hover:bg-fa-cream/85 disabled:opacity-50"
                            >
                                <PlusCircle size={12} weight="bold" />
                                {submitting ? "Publishing…" : "Publish signal"}
                            </button>
                        </div>
                    </form>

                    <div>
                        <div className="font-mono text-[10px] uppercase tracking-widest text-white/50 mb-2">
                            Active signals {loading && "· loading"}
                        </div>
                        {signals.length === 0 ? (
                            <div className="font-mono text-[11px] text-white/40 italic">
                                No signals yet.
                            </div>
                        ) : (
                            <div className="space-y-1.5" data-testid="news-signal-list">
                                {signals.map((s) => (
                                    <div
                                        key={s.id}
                                        className="border border-white/10 bg-black/50 px-3 py-2 flex items-start justify-between gap-2"
                                        data-testid={`news-signal-row-${s.id}`}
                                    >
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span className="font-mono text-[9px] uppercase tracking-widest text-fa-cream border border-fa-cream/40 px-1 py-0.5">
                                                    {s.topic.replace(/_/g, " ")}
                                                </span>
                                                {s.team_name && (
                                                    <span className="font-mono text-[10px] text-white/70">
                                                        {s.team_name}
                                                    </span>
                                                )}
                                                <span className="font-mono text-[10px] text-white/40">
                                                    · {s.source}
                                                </span>
                                                <span className="font-mono text-[10px] text-white/40">
                                                    · impact {s.impact_score}
                                                </span>
                                            </div>
                                            <div className="font-display font-bold text-sm mt-0.5">
                                                {s.headline}
                                            </div>
                                            <div className="font-mono text-[11px] text-white/60 leading-snug mt-0.5">
                                                {s.summary}
                                            </div>
                                        </div>
                                        {user && (
                                            <button
                                                type="button"
                                                onClick={() => remove(s.id)}
                                                data-testid={`news-signal-delete-${s.id}`}
                                                className="text-white/40 hover:text-red-400 p-1"
                                                title="Delete signal"
                                            >
                                                <Trash size={13} weight="bold" />
                                            </button>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </section>
    );
}

const inputCls = "w-full bg-black/60 border border-white/10 focus:border-fa-cream/60 outline-none text-white font-mono text-xs px-2 py-1.5";
const selectCls = `${inputCls} appearance-none`;

function Field({ label, children, full }) {
    return (
        <label className={`flex flex-col gap-1 ${full ? "md:col-span-2" : ""}`}>
            <span className="font-mono text-[10px] uppercase tracking-widest text-white/50">
                {label}
            </span>
            {children}
        </label>
    );
}
